import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  BarChart3,
  TrendingUp,
  TrendingDown,
  PieChart,
  Globe,
  Server,
  Download,
  Eye,
  Calendar,
  AlertTriangle,
  RefreshCw,
  Filter,
  Users,
  HardDrive,
  Clock,
  CheckCircle
} from 'lucide-react';
import VersionDistributionChart from './analytics/VersionDistributionChart';
import UsageMetricsCards from './analytics/UsageMetricsCards';
import AccessAnalytics from './analytics/AccessAnalytics';
import StorageAnalytics from './analytics/StorageAnalytics';
import GeographicDistribution from './analytics/GeographicDistribution';

/**
 * MaterialsAnalytics - Comprehensive analytics dashboard for materials usage and version distribution
 * 
 * Provides insights into:
 * - Version adoption rates and distribution
 * - Download statistics and access patterns
 * - Geographic distribution of materials access
 * - Storage usage and costs
 * - Performance metrics (CDN cache hit rates, response times)
 * - Most accessed materials and programs
 * 
 * Features:
 * - Interactive charts with filtering by date range, program, and session
 * - Real-time metrics and historical trends
 * - Performance monitoring and alerting
 * - Mobile-responsive design
 */
const MaterialsAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('30days');
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [selectedSession, setSelectedSession] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalDownloads: 0,
      uniqueUsers: 0,
      activeVersions: 0,
      programsCovered: 0,
      topMaterials: [],
      recentActivity: []
    },
    versions: {
      adoptionRates: [],
      versionTimeline: [],
      migrationProgress: {}
    },
    usage: {
      downloadTrends: [],
      accessPatterns: [],
      popularTimes: {},
      deviceBreakdown: {}
    },
    geographic: {
      accessByCountry: [],
      topRegions: [],
      languageDistribution: {}
    },
    storage: {
      totalUsage: 0,
      costBreakdown: {},
      growthTrends: [],
      compressionSavings: 0
    },
    performance: {
      cdnCacheHitRate: 0,
      averageResponseTime: 0,
      uptime: 0,
      errorRate: 0,
      performanceTrends: []
    }
  });

  const [filterOptions, setFilterOptions] = useState({
    programs: [],
    sessions: [],
    dateRanges: [
      { value: '7days', label: 'Last 7 Days' },
      { value: '30days', label: 'Last 30 Days' },
      { value: '90days', label: 'Last 90 Days' },
      { value: '1year', label: 'Last Year' },
      { value: 'all', label: 'All Time' }
    ]
  });

  // Load analytics data
  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange, selectedProgram, selectedSession]);

  const loadAnalyticsData = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Mock analytics data - replace with actual API calls
      const mockData = generateMockAnalyticsData(dateRange, selectedProgram, selectedSession);
      setAnalyticsData(mockData);

      // Generate filter options
      const mockFilterOptions = {
        programs: [
          { value: 'all', label: 'All Programs' },
          { value: 'scb', label: 'Self-Care for Burnout (SCB)' },
          { value: 'hbw', label: 'Healthy Boundaries at Work (HBW)' },
          { value: 'le', label: 'Leadership Essentials (LE)' },
          { value: 'ws', label: 'Workplace Skills (WS)' }
        ],
        sessions: [
          { value: 'all', label: 'All Sessions' },
          { value: 'session1', label: 'Session 1' },
          { value: 'session2', label: 'Session 2' },
          { value: 'session3', label: 'Session 3' },
          { value: 'session4', label: 'Session 4' }
        ],
        dateRanges: filterOptions.dateRanges
      };
      setFilterOptions(mockFilterOptions);

    } catch (err) {
      console.error('Failed to load analytics data:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Generate mock data based on filters
  const generateMockAnalyticsData = (range, program, session) => {
    const baseMultiplier = program === 'all' ? 1 : 0.3;
    const sessionMultiplier = session === 'all' ? 1 : 0.25;
    const rangeMultiplier = {
      '7days': 0.2,
      '30days': 0.6,
      '90days': 0.9,
      '1year': 1.0,
      'all': 1.2
    }[range] || 1.0;

    const multiplier = baseMultiplier * sessionMultiplier * rangeMultiplier;

    return {
      overview: {
        totalDownloads: Math.floor(12547 * multiplier),
        uniqueUsers: Math.floor(2341 * multiplier),
        activeVersions: Math.floor(24 * multiplier),
        programsCovered: program === 'all' ? 8 : 1,
        topMaterials: [
          {
            name: 'Self-Care Assessment Workbook.pdf',
            program: 'Self-Care for Burnout',
            downloads: Math.floor(834 * multiplier),
            version: 'v2.1.0',
            trend: 'up'
          },
          {
            name: 'Boundary Setting Framework.pptx',
            program: 'Healthy Boundaries at Work',
            downloads: Math.floor(723 * multiplier),
            version: 'v1.3.2',
            trend: 'up'
          },
          {
            name: 'Leadership Competency Matrix.xlsx',
            program: 'Leadership Essentials',
            downloads: Math.floor(654 * multiplier),
            version: 'v3.0.1',
            trend: 'stable'
          }
        ],
        recentActivity: [
          {
            id: 1,
            action: 'New version deployed',
            program: 'Self-Care for Burnout',
            session: 'Session 2',
            version: 'v2.1.0',
            timestamp: '2024-12-10T14:30:00Z',
            impact: 'high'
          },
          {
            id: 2,
            action: 'High download activity',
            program: 'Leadership Essentials',
            session: 'Session 4',
            version: 'v3.0.1',
            timestamp: '2024-12-10T12:15:00Z',
            impact: 'medium'
          }
        ]
      },
      versions: {
        adoptionRates: [
          { version: 'v3.0.1', adoption: 78, programs: 3, sessions: 12 },
          { version: 'v2.1.0', adoption: 65, programs: 2, sessions: 8 },
          { version: 'v1.3.2', adoption: 45, programs: 2, sessions: 6 },
          { version: 'v2.0.3', adoption: 23, programs: 1, sessions: 4 }
        ],
        versionTimeline: generateVersionTimelineData(range, multiplier),
        migrationProgress: {
          'v3.0.1': { total: 15, migrated: 12, percentage: 80 },
          'v2.1.0': { total: 10, migrated: 8, percentage: 80 },
          'v1.3.2': { total: 8, migrated: 6, percentage: 75 }
        }
      },
      usage: {
        downloadTrends: generateDownloadTrendsData(range, multiplier),
        accessPatterns: generateAccessPatternsData(multiplier),
        popularTimes: {
          morning: Math.floor(35 * multiplier),
          afternoon: Math.floor(45 * multiplier),
          evening: Math.floor(20 * multiplier)
        },
        deviceBreakdown: {
          desktop: Math.floor(65 * multiplier),
          mobile: Math.floor(25 * multiplier),
          tablet: Math.floor(10 * multiplier)
        }
      },
      geographic: {
        accessByCountry: [
          { country: 'Canada', downloads: Math.floor(8234 * multiplier), percentage: 66 },
          { country: 'United States', downloads: Math.floor(2341 * multiplier), percentage: 19 },
          { country: 'United Kingdom', downloads: Math.floor(1123 * multiplier), percentage: 9 },
          { country: 'Australia', downloads: Math.floor(567 * multiplier), percentage: 4 },
          { country: 'Other', downloads: Math.floor(282 * multiplier), percentage: 2 }
        ],
        topRegions: [
          { region: 'Ontario', downloads: Math.floor(4123 * multiplier) },
          { region: 'British Columbia', downloads: Math.floor(2341 * multiplier) },
          { region: 'Quebec', downloads: Math.floor(1234 * multiplier) },
          { region: 'Alberta', downloads: Math.floor(901 * multiplier) }
        ],
        languageDistribution: {
          english: Math.floor(85 * multiplier),
          french: Math.floor(12 * multiplier),
          other: Math.floor(3 * multiplier)
        }
      },
      storage: {
        totalUsage: 15.7 * multiplier,
        costBreakdown: {
          storage: 45.23 * multiplier,
          bandwidth: 78.91 * multiplier,
          requests: 23.45 * multiplier
        },
        growthTrends: generateStorageGrowthData(range, multiplier),
        compressionSavings: 23.5 * multiplier
      },
      performance: {
        cdnCacheHitRate: 94.2,
        averageResponseTime: 156,
        uptime: 99.8,
        errorRate: 0.02,
        performanceTrends: generatePerformanceData(range)
      }
    };
  };

  // Helper functions to generate trend data
  const generateVersionTimelineData = (range, multiplier) => {
    const days = { '7days': 7, '30days': 30, '90days': 90, '1year': 365, 'all': 730 }[range] || 30;
    return Array.from({ length: Math.min(days, 30) }, (_, i) => ({
      date: new Date(Date.now() - (days - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      'v3.0.1': Math.floor((20 + Math.random() * 10) * multiplier),
      'v2.1.0': Math.floor((15 + Math.random() * 8) * multiplier),
      'v1.3.2': Math.floor((10 + Math.random() * 5) * multiplier)
    }));
  };

  const generateDownloadTrendsData = (range, multiplier) => {
    const days = { '7days': 7, '30days': 30, '90days': 90, '1year': 365, 'all': 730 }[range] || 30;
    return Array.from({ length: Math.min(days, 30) }, (_, i) => ({
      date: new Date(Date.now() - (days - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      downloads: Math.floor((100 + Math.random() * 200) * multiplier),
      uniqueUsers: Math.floor((25 + Math.random() * 50) * multiplier)
    }));
  };

  const generateAccessPatternsData = (multiplier) => {
    return Array.from({ length: 24 }, (_, hour) => ({
      hour,
      accesses: Math.floor((Math.sin((hour - 9) * Math.PI / 12) * 50 + 60) * multiplier)
    }));
  };

  const generateStorageGrowthData = (range, multiplier) => {
    const months = { '7days': 1, '30days': 3, '90days': 6, '1year': 12, 'all': 24 }[range] || 6;
    return Array.from({ length: months }, (_, i) => ({
      month: new Date(Date.now() - (months - i) * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0].substring(0, 7),
      storage: Math.floor((10 + i * 1.5) * multiplier * 100) / 100,
      cost: Math.floor((25 + i * 8) * multiplier * 100) / 100
    }));
  };

  const generatePerformanceData = (range) => {
    const days = { '7days': 7, '30days': 30, '90days': 90, '1year': 365, 'all': 730 }[range] || 30;
    return Array.from({ length: Math.min(days, 30) }, (_, i) => ({
      date: new Date(Date.now() - (days - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      cacheHitRate: 92 + Math.random() * 6,
      responseTime: 120 + Math.random() * 80,
      uptime: 99.5 + Math.random() * 0.5
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>

        {/* Filter skeleton */}
        <div className="flex flex-wrap gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-32" />
          ))}
        </div>

        {/* Tabs skeleton */}
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <Skeleton className="h-5 w-5" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <Skeleton className="h-8 w-16 mb-1" />
                  <Skeleton className="h-3 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-600 font-medium">Failed to load analytics</p>
          <p className="text-gray-500 text-sm mt-1">{error}</p>
          <Button 
            onClick={() => loadAnalyticsData()} 
            variant="outline" 
            className="mt-4"
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900">
            Materials Analytics
          </h2>
          <p className="text-gray-600 mt-2">
            Comprehensive insights into materials usage, version adoption, and performance metrics
          </p>
        </div>
        <Button 
          onClick={() => loadAnalyticsData(true)} 
          variant="outline" 
          size="sm"
          disabled={refreshing}
          className="flex items-center space-x-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filters:</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            {filterOptions.dateRanges.map(range => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <Users className="h-4 w-4 text-gray-500" />
          <select 
            value={selectedProgram} 
            onChange={(e) => setSelectedProgram(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            {filterOptions.programs.map(program => (
              <option key={program.value} value={program.value}>
                {program.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <CheckCircle className="h-4 w-4 text-gray-500" />
          <select 
            value={selectedSession} 
            onChange={(e) => setSelectedSession(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            {filterOptions.sessions.map(session => (
              <option key={session.value} value={session.value}>
                {session.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Analytics Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-6">
          <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
          <TabsTrigger value="versions" className="text-xs">Versions</TabsTrigger>
          <TabsTrigger value="usage" className="text-xs">Usage</TabsTrigger>
          <TabsTrigger value="geographic" className="text-xs">Geographic</TabsTrigger>
          <TabsTrigger value="storage" className="text-xs">Storage</TabsTrigger>
          <TabsTrigger value="performance" className="text-xs">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <UsageMetricsCards data={analyticsData.overview} />
          
          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Recent Activity</span>
              </CardTitle>
              <CardDescription>
                Latest materials activity and version deployments
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analyticsData.overview.recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 mt-0.5">
                      {activity.impact === 'high' ? (
                        <TrendingUp className="h-4 w-4 text-red-600" />
                      ) : activity.impact === 'medium' ? (
                        <TrendingUp className="h-4 w-4 text-yellow-600" />
                      ) : (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-sm text-gray-900">
                          {activity.action}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {activity.version}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        {activity.program} → {activity.session}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="versions" className="space-y-6">
          <VersionDistributionChart data={analyticsData.versions} />
        </TabsContent>

        <TabsContent value="usage" className="space-y-6">
          <AccessAnalytics data={analyticsData.usage} />
        </TabsContent>

        <TabsContent value="geographic" className="space-y-6">
          <GeographicDistribution data={analyticsData.geographic} />
        </TabsContent>

        <TabsContent value="storage" className="space-y-6">
          <StorageAnalytics data={analyticsData.storage} />
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Server className="h-5 w-5" />
                <span>Performance Metrics</span>
              </CardTitle>
              <CardDescription>
                CDN performance, response times, and system health
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-4 border rounded-lg">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-gray-500">Cache Hit Rate</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {analyticsData.performance.cdnCacheHitRate.toFixed(1)}%
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Excellent performance</p>
                </div>

                <div className="text-center p-4 border rounded-lg">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Clock className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-500">Avg Response Time</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {analyticsData.performance.averageResponseTime}ms
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Global average</p>
                </div>

                <div className="text-center p-4 border rounded-lg">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Server className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-gray-500">Uptime</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {analyticsData.performance.uptime.toFixed(1)}%
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
                </div>

                <div className="text-center p-4 border rounded-lg">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <span className="text-sm font-medium text-gray-500">Error Rate</span>
                  </div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {analyticsData.performance.errorRate.toFixed(2)}%
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Within target</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MaterialsAnalytics;